/**
 * Schlankes Live-Polling für Views, die ihre Daten per REST holen (z.B. das
 * Dashboard: `dashboard_metrics`). Bewusst KEIN setInterval-Sammelsurium:
 * - selbst-rescheduelnde setTimeout-Kette → niemals überlappende Requests
 *   (ein laufender Task blockt den nächsten Tick, statt zu stapeln),
 * - sauberes Cleanup beim Unmount (Timer wird gestoppt, kein Leak),
 * - bei verstecktem Tab (`document.hidden`) wird der Task übersprungen UND der
 *   nächste Tick langsamer terminiert (kein Fetch-Spam im Hintergrund).
 *
 * Die eigentliche Logik steckt in `createPoller` (DOM-/Timer-frei injizierbar,
 * deterministisch testbar). Der `usePolling`-Hook ist nur der dünne Preact-
 * Wrapper drumherum.
 */

import { useEffect, useRef } from "preact/hooks";
import { useSignal } from "@preact/signals";

/** Nächste Tick-Verzögerung: sichtbar = Basis, versteckt = Basis × Faktor. */
export function nextPollDelay(intervalMs: number, hidden: boolean, hiddenFactor = 4): number {
  return hidden ? intervalMs * hiddenFactor : intervalMs;
}

export interface PollerEnv {
  setTimer: (cb: () => void, ms: number) => number;
  clearTimer: (id: number) => void;
  isHidden: () => boolean;
}

export interface Poller {
  start: () => void;
  stop: () => void;
}

/**
 * Erzeugt einen Poller. `task` wird wiederholt aufgerufen; ein noch laufender
 * Task verhindert den nächsten (kein Stapeln). `stop()` beendet endgültig.
 */
export function createPoller(
  task: () => unknown | Promise<unknown>,
  intervalMs: number,
  env: PollerEnv,
  hiddenFactor = 4,
): Poller {
  let timerId: number | null = null;
  let stopped = false;
  let inFlight = false;

  function schedule(): void {
    if (stopped) return;
    timerId = env.setTimer(run, nextPollDelay(intervalMs, env.isHidden(), hiddenFactor));
  }

  async function run(): Promise<void> {
    if (stopped) return;
    // Überlappung vermeiden: nur feuern, wenn kein Task läuft und der Tab sichtbar ist.
    if (!inFlight && !env.isHidden()) {
      inFlight = true;
      try {
        await task();
      } catch {
        /* Fehler werden vom Task selbst behandelt; Poller läuft weiter. */
      } finally {
        inFlight = false;
      }
    }
    schedule();
  }

  return {
    start(): void {
      if (stopped || timerId !== null) return;
      schedule();
    },
    stop(): void {
      stopped = true;
      if (timerId !== null) {
        env.clearTimer(timerId);
        timerId = null;
      }
    },
  };
}

/**
 * Leichter UI-Uhr-Ticker für relative Zeitanzeigen (z.B. "Last Signal" live
 * hochzählen, OHNE jede Sekunde die API zu fetchen). Gibt `Date.now()` zurück und
 * aktualisiert es alle `intervalMs`. Selbst-rescheduelnd mit Cleanup beim Unmount;
 * im versteckten Tab 4× langsamer (kein unnötiger Re-render im Hintergrund).
 */
export function useDashboardClock(intervalMs = 1000): number {
  const now = useSignal(Date.now());
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const hidden = () => typeof document !== "undefined" && document.hidden === true;
    function tick(): void {
      if (cancelled) return;
      now.value = Date.now();
      timerId = setTimeout(tick, nextPollDelay(intervalMs, hidden()));
    }
    timerId = setTimeout(tick, nextPollDelay(intervalMs, hidden()));
    return () => {
      cancelled = true;
      if (timerId !== undefined) clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);
  return now.value;
}

/**
 * Pollt `task` alle `intervalMs`. Initialer Load bleibt Sache des Aufrufers
 * (der Poller feuert erst nach dem ersten Intervall) — so gibt es kein doppeltes
 * Mount-Fetch. Restart bei geänderten `deps`.
 */
export function usePolling(
  task: () => unknown | Promise<unknown>,
  intervalMs: number,
  deps: unknown[] = [],
): void {
  const taskRef = useRef(task);
  taskRef.current = task;

  useEffect(() => {
    const poller = createPoller(
      () => taskRef.current(),
      intervalMs,
      {
        setTimer: (cb, ms) => globalThis.setTimeout(cb, ms) as unknown as number,
        clearTimer: (id) => globalThis.clearTimeout(id),
        isHidden: () => typeof document !== "undefined" && document.hidden === true,
      },
    );
    poller.start();
    return () => poller.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
